import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, Eye, FileText, CheckCircle, XCircle, Clock,
  Send, Download, Trash2, RefreshCw, Filter, Building2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import api, { vendorsAPI, warehousesAPI } from '@/services/api';
import { formatDate, formatDateOnly } from '@/utils/dateFormat';
import { Checkbox } from '@/components/ui/checkbox';
import BulkDeleteBar from '@/components/hq/BulkDeleteBar';

const statusConfig = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700', icon: FileText },
  pending_approval: { label: 'Pending Approval', color: 'bg-amber-100 text-amber-700', icon: Clock },
  approved: { label: 'Approved', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: XCircle }
};

const PurchaseOrderListPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('');
  const [warehouseFilter, setWarehouseFilter] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  const [vendors, setVendors] = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  // Action dialogs
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter && statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (vendorFilter) {
        params.vendor_id = vendorFilter;
      }
      if (warehouseFilter) {
        params.warehouse_id = warehouseFilter;
      }
      if (search) {
        params.q = search;
      }

      const res = await api.get('/po-v2', { params });
      setOrders(res.data || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch orders', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, vendorFilter, warehouseFilter, search, toast]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const [vendorsRes, warehousesRes] = await Promise.all([
          vendorsAPI.list(),
          warehousesAPI.list()
        ]);
        setVendors(vendorsRes.data || []);
        setWarehouses(warehousesRes.data?.items || warehousesRes.data || []);
      } catch (error) {
        console.error('Failed to fetch lookups:', error);
      }
    };
    fetchLookups();
  }, []);

  // Filter orders by tab
  const getFilteredOrders = () => {
    let filtered = orders;

    switch (activeTab) {
      case 'draft':
        filtered = orders.filter(o => o.status === 'draft');
        break;
      case 'pending':
        filtered = orders.filter(o => o.status === 'pending_approval');
        break;
      case 'approved':
        filtered = orders.filter(o => o.status === 'approved');
        break;
      case 'completed':
        filtered = orders.filter(o => o.status === 'completed');
        break;
      case 'cancelled':
        filtered = orders.filter(o => o.status === 'cancelled');
        break;
      default:
        break;
    }

    return filtered;
  };

  const filteredOrders = getFilteredOrders();

  // Counts for tabs
  const counts = {
    all: orders.length,
    draft: orders.filter(o => o.status === 'draft').length,
    pending: orders.filter(o => o.status === 'pending_approval').length,
    approved: orders.filter(o => o.status === 'approved').length,
    completed: orders.filter(o => o.status === 'completed').length,
    cancelled: orders.filter(o => o.status === 'cancelled').length
  };

  // Actions
  const handleApprove = async (po) => {
    setActionLoading(true);
    try {
      await api.put(`/po-v2/${po.id}/approve`);
      toast({ title: 'Success', description: 'PO approved' });
      fetchOrders();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to approve', variant: 'destructive' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubmitForApproval = async (po) => {
    setActionLoading(true);
    try {
      await api.put(`/po-v2/${po.id}/submit-for-approval`);
      toast({ title: 'Success', description: 'PO submitted for approval' });
      fetchOrders();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to submit', variant: 'destructive' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!selectedPO) return;
    setActionLoading(true);
    try {
      await api.put(`/po-v2/${selectedPO.id}/cancel`);
      toast({ title: 'Success', description: 'PO cancelled' });
      setCancelDialogOpen(false);
      fetchOrders();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to cancel', variant: 'destructive' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedPO) return;
    setActionLoading(true);
    try {
      await api.delete(`/po-v2/${selectedPO.id}`);
      toast({ title: 'Success', description: 'PO deleted' });
      setDeleteDialogOpen(false);
      fetchOrders();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to delete', variant: 'destructive' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleConvertToGRN = (po) => {
    navigate(`/hq/purchase/grn/local/new?po_id=${po.id}`);
  };

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen" data-testid="po-list-page">
      <Toaster />

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Purchase Orders</h1>
          <p className="text-gray-500">Manage vendor purchase orders</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchOrders} data-testid="po-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button 
            onClick={() => navigate('/hq/purchase/orders/new')}
            className="bg-teal-500 hover:bg-teal-600"
            data-testid="po-create-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> New PO
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
        <TabsList className="bg-white border">
          <TabsTrigger value="all" data-testid="tab-all">
            All ({counts.all})
          </TabsTrigger>
          <TabsTrigger value="draft" data-testid="tab-draft">
            Draft ({counts.draft})
          </TabsTrigger>
          <TabsTrigger value="pending" data-testid="tab-pending">
            Pending ({counts.pending})
          </TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved">
            Approved ({counts.approved})
          </TabsTrigger>
          <TabsTrigger value="completed" data-testid="tab-completed">
            Completed ({counts.completed})
          </TabsTrigger>
          <TabsTrigger value="cancelled" data-testid="tab-cancelled">
            Cancelled ({counts.cancelled})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by PO number or vendor..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="po-search-input"
                />
              </div>
            </div>
            <Select value={vendorFilter} onValueChange={setVendorFilter}>
              <SelectTrigger className="w-[180px]" data-testid="po-vendor-filter">
                <SelectValue placeholder="All Vendors" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Vendors</SelectItem>
                {vendors.map(v => (
                  <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={warehouseFilter} onValueChange={setWarehouseFilter}>
              <SelectTrigger className="w-[180px]" data-testid="po-warehouse-filter">
                <SelectValue placeholder="All Warehouses" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Warehouses</SelectItem>
                {warehouses.map(w => (
                  <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Orders Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-500"></div>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No purchase orders found</p>
              <Button 
                className="mt-4" 
                onClick={() => navigate('/hq/purchase/orders/new')}
              >
                Create your first PO
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              {selectedIds.length > 0 && (
                <div className="p-3 border-b border-gray-200">
                  <BulkDeleteBar
                    selectedIds={selectedIds}
                    entityLabel="purchase order"
                    endpoint="/hq/bulk-delete/purchase-orders"
                    supportModes
                    onCleared={() => { setSelectedIds([]); fetchOrders(); }}
                  />
                </div>
              )}
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="w-10">
                      <Checkbox
                        checked={filteredOrders.length > 0 && selectedIds.length === filteredOrders.length}
                        onCheckedChange={(c) => setSelectedIds(c ? filteredOrders.map(p => p.id) : [])}
                        data-testid="po-select-all"
                      />
                    </TableHead>
                    <TableHead>PO Number</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Warehouse</TableHead>
                    <TableHead className="text-center">Items</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrders.map(po => {
                    const StatusIcon = statusConfig[po.status]?.icon || FileText;
                    const isInternational = po.currency !== 'INR';
                    
                    return (
                      <TableRow key={po.id} className="hover:bg-gray-50" data-testid={`po-row-${po.id}`}>
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.includes(po.id)}
                            onCheckedChange={(c) => setSelectedIds(prev => c ? [...prev, po.id] : prev.filter(x => x !== po.id))}
                            data-testid={`po-select-${po.id}`}
                          />
                        </TableCell>
                        <TableCell>
                          <div>
                            <span className="font-mono text-teal-600">{po.po_number}</span>
                            {isInternational && (
                              <Badge className="ml-2 bg-amber-100 text-amber-700 text-xs">Intl</Badge>
                            )}
                          </div>
                          {po.grn_number && (
                            <span className="text-xs text-gray-400">GRN: {po.grn_number}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-gray-400" />
                            <div>
                              <p className="font-medium text-sm">
                                {po.vendor_snapshot?.vendor_name || po.vendor_name || 'Unknown Vendor'}
                              </p>
                              <p className="text-xs text-gray-400">
                                {po.vendor_snapshot?.vendor_code || po.vendor_code || ''}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">{po.warehouse_name}</span>
                        </TableCell>
                        <TableCell className="text-center">
                          {po.items?.length || 0}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-medium">
                            {isInternational ? `${po.currency} ` : '₹'}
                            {(po.grand_total || 0).toLocaleString()}
                          </span>
                          {isInternational && (
                            <div className="text-xs text-gray-400">
                              Rate: {po.conversion_rate}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className={statusConfig[po.status]?.color}>
                            {statusConfig[po.status]?.label || po.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-gray-500">
                            {formatDateOnly(po.order_date || po.created_at)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => navigate(`/hq/purchase/orders/${po.id}`)}
                              data-testid={`po-view-${po.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            
                            {po.status === 'draft' && (
                              <>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => navigate(`/hq/purchase/orders/${po.id}/edit`)}
                                  className="text-blue-600"
                                  data-testid={`po-edit-${po.id}`}
                                >
                                  Edit
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleSubmitForApproval(po)}
                                  className="text-amber-600"
                                  disabled={actionLoading}
                                  data-testid={`po-submit-${po.id}`}
                                >
                                  <Send className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => { setSelectedPO(po); setDeleteDialogOpen(true); }}
                                  className="text-red-500"
                                  data-testid={`po-delete-${po.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            
                            {po.status === 'pending_approval' && (
                              <>
                                <Button 
                                  size="sm"
                                  onClick={() => handleApprove(po)}
                                  className="bg-green-500 hover:bg-green-600 text-white"
                                  disabled={actionLoading}
                                  data-testid={`po-approve-${po.id}`}
                                >
                                  Approve
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => { setSelectedPO(po); setCancelDialogOpen(true); }}
                                  className="text-red-500"
                                  data-testid={`po-cancel-${po.id}`}
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            
                            {po.status === 'approved' && !po.grn_id && (
                              <Button 
                                size="sm"
                                onClick={() => handleConvertToGRN(po)}
                                className="bg-teal-500 hover:bg-teal-600 text-white"
                                data-testid={`po-to-grn-${po.id}`}
                              >
                                Create GRN
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Purchase Order?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete PO &quot;{selectedPO?.po_number}&quot;. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
              disabled={actionLoading}
            >
              {actionLoading ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel Confirmation */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Purchase Order?</AlertDialogTitle>
            <AlertDialogDescription>
              This will cancel PO &quot;{selectedPO?.po_number}&quot;. The PO will be marked as cancelled and cannot be processed further.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep PO</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleCancel}
              className="bg-red-500 hover:bg-red-600"
              disabled={actionLoading}
            >
              {actionLoading ? 'Cancelling...' : 'Cancel PO'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PurchaseOrderListPage;
